import { getPharmacySettings, getReceptionSettings } from "@/core/admin/establishment-store";
import { getPrescriptionById } from "@/core/clinical/clinical-store";
import { handleBillingDocumentPaidForPrescription } from "@/core/clinical/prescription-fulfillment";
import { handleBillingDocumentPaidForConsumableCharge } from "@/core/patient-consumables/consumable-charge-fulfillment";
import { getPatientConsumableChargeById } from "@/core/patient-consumables/patient-consumable-store";
import { getCoverageCaseForPrescription } from "@/core/insurance/coverage-case-flow";
import { getCoverageCase } from "@/core/insurance/coverage-case-store";
import { getLabOrderById } from "@/core/laboratory/lab-store";
import { getImagingExamById } from "@/core/imaging/imaging-store";
import { resolveProductPrice } from "@/core/inventory/pharmacy-stock-sync";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { getPatientById } from "@/core/patients/patient-store";
import { hasThirdPartyCoverage, patientFullName } from "@/core/patients/types";
import { getInsurerById } from "@/core/insurance/insurer-store";
import { publishBillingDocumentToHub } from "@/core/insurance/hub-invoice";
import { getActivePartnershipForInsurer } from "@/core/insurance/partnership-store";
import {
  creditTreasuryAccount,
  getTreasuryAccount,
} from "@/core/treasury/treasury-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import {
  apiListBillingDocuments,
  apiSyncBillingDocument,
} from "@/core/hospitol-api-client";
import {
  markPharmacySotMigratedIfRemoteConfirmed,
  probeHospitolDurableConfigured,
} from "@/core/pharmacy/pharmacy-durable-sot";
import type { PaymentMethod } from "@/core/treasury/types";
import type {
  BillingDocument,
  BillingDocumentLine,
  BillingDocumentStatus,
  BillingPayment,
  BillingPaymentAttachment,
  PayerType,
} from "./types";

const DOCS_KEY = "hospitol.billingDocuments";
const PRO_SEQ_KEY = "hospitol.billingProformaSeq";

export class BillingInsurerHubError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BillingInsurerHubError";
  }
}

function lineId(): string {
  return `bdl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function normalizeDocument(raw: Record<string, unknown>): BillingDocument {
  const prescriptionId = raw.prescriptionId;
  return {
    id: String(raw.id ?? ""),
    source: (raw.source as BillingDocument["source"]) ?? (prescriptionId ? "prescription" : "consultation"),
    kind: (raw.kind as BillingDocument["kind"]) ?? "invoice",
    status: (raw.status as BillingDocumentStatus) ?? "pending",
    number: String(raw.number ?? ""),
    patientId: String(raw.patientId ?? ""),
    prescriptionId: prescriptionId ? String(prescriptionId) : null,
    prescriptionLineIds: Array.isArray(raw.prescriptionLineIds)
      ? (raw.prescriptionLineIds as string[])
      : [],
    labOrderId: raw.labOrderId ? String(raw.labOrderId) : null,
    imagingExamId: raw.imagingExamId ? String(raw.imagingExamId) : null,
    surgeryCaseId: raw.surgeryCaseId ? String(raw.surgeryCaseId) : null,
    consumableChargeId: raw.consumableChargeId ? String(raw.consumableChargeId) : null,
    consultationId: raw.consultationId ? String(raw.consultationId) : null,
    coverageCaseId: raw.coverageCaseId ? String(raw.coverageCaseId) : null,
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    stayId: raw.stayId ? String(raw.stayId) : null,
    serviceLabel: String(raw.serviceLabel ?? ""),
    payerType: (raw.payerType as PayerType) ?? "patient",
    payerName: String(raw.payerName ?? ""),
    insurerId: raw.insurerId ? String(raw.insurerId) : null,
    customer: (raw.customer as BillingDocument["customer"]) ?? { name: "", phone: "", address: "" },
    lines: Array.isArray(raw.lines) ? (raw.lines as BillingDocumentLine[]) : [],
    subtotal: Number(raw.subtotal ?? 0),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    issuedAt: raw.issuedAt ? String(raw.issuedAt) : null,
    proformaValidUntil: raw.proformaValidUntil ? String(raw.proformaValidUntil) : null,
    convertedFromId: raw.convertedFromId ? String(raw.convertedFromId) : null,
    hubInvoiceId: raw.hubInvoiceId ? String(raw.hubInvoiceId) : null,
    hubPublishedAt: raw.hubPublishedAt ? String(raw.hubPublishedAt) : null,
    payment: (raw.payment as BillingPayment | null) ?? null,
  };
}

function resolveBillingPayerFromPatient(patientId: string): {
  payerType: PayerType;
  payerName: string;
  insurerId: string | null;
} {
  const patient = getPatientById(patientId);
  if (!patient) {
    return { payerType: "patient", payerName: "", insurerId: null };
  }

  if (patient.coverageType === "insurer" && patient.insurerId) {
    const insurer = getInsurerById(patient.insurerId);
    return {
      payerType: "insurer",
      payerName: insurer?.name ?? patient.coverageName,
      insurerId: patient.insurerId,
    };
  }

  if (patient.coverageType === "company" && hasThirdPartyCoverage(patient)) {
    return {
      payerType: "company",
      payerName: patient.coverageName,
      insurerId: null,
    };
  }

  return {
    payerType: "patient",
    payerName: patientFullName(patient),
    insurerId: null,
  };
}

function readDocuments(): BillingDocument[] {
  const raw = readJson<unknown>(DOCS_KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      try {
        return normalizeDocument(item as Record<string, unknown>);
      } catch {
        return null;
      }
    })
    .filter((doc): doc is BillingDocument => doc !== null);
}

function replaceDocumentsCache(docs: BillingDocument[]): void {
  writeJson(DOCS_KEY, docs);
  window.dispatchEvent(new CustomEvent("hospitol:billing-documents"));
}

function replicateBillingDocumentToApi(doc: BillingDocument): void {
  void apiSyncBillingDocument(doc);
}

/** Pull billing documents from API — durable SoT when DATABASE_URL configured (WP-C3). */
export async function syncBillingDocumentsFromApi(): Promise<boolean> {
  const remote = await apiListBillingDocuments();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();
  const normalized = remote.map((d) =>
    normalizeDocument(d as unknown as Record<string, unknown>),
  );

  if (normalized.length > 0) {
    replaceDocumentsCache(normalized);
    markPharmacySotMigratedIfRemoteConfirmed("billingDocuments", durable, normalized.length);
    return true;
  }

  const local = readDocuments();
  if (local.length > 0) {
    for (const doc of local) {
      await apiSyncBillingDocument(doc);
    }
    const again = await apiListBillingDocuments();
    if (again === null) return false;
    if (again.length > 0) {
      replaceDocumentsCache(
        again.map((d) => normalizeDocument(d as unknown as Record<string, unknown>)),
      );
      markPharmacySotMigratedIfRemoteConfirmed("billingDocuments", durable, again.length);
    }
    return true;
  }

  return true;
}

function nextProformaNumber(): string {
  const seq = Number(getStorageItem(PRO_SEQ_KEY) ?? "0") + 1;
  setStorageItem(PRO_SEQ_KEY, String(seq));
  return `PRO-${String(seq).padStart(5, "0")}`;
}

function nextInvoiceNumber(): string {
  const { invoicePrefix } = getPharmacySettings();
  const seqKey = "pharmacy.invoiceSeq";
  const seq = Number(getStorageItem(seqKey) ?? "0") + 1;
  setStorageItem(seqKey, String(seq));
  const prefix = invoicePrefix.trim().toUpperCase() || "FAC";
  return `${prefix}-${String(seq).padStart(5, "0")}`;
}

export function listBillingDocuments(filter?: {
  status?: BillingDocumentStatus | BillingDocumentStatus[];
  prescriptionId?: string;
  admissionId?: string;
  labOrderId?: string;
  imagingExamId?: string;
  surgeryCaseId?: string;
  consumableChargeId?: string;
  consultationId?: string;
  coverageCaseId?: string;
  source?: BillingDocument["source"];
}): BillingDocument[] {
  let list = readDocuments();
  if (filter?.prescriptionId) {
    list = list.filter((d) => d.prescriptionId === filter.prescriptionId);
  }
  if (filter?.admissionId) {
    list = list.filter((d) => d.admissionId === filter.admissionId);
  }
  if (filter?.labOrderId) {
    list = list.filter((d) => d.labOrderId === filter.labOrderId);
  }
  if (filter?.imagingExamId) {
    list = list.filter((d) => d.imagingExamId === filter.imagingExamId);
  }
  if (filter?.surgeryCaseId) {
    list = list.filter((d) => d.surgeryCaseId === filter.surgeryCaseId);
  }
  if (filter?.consumableChargeId) {
    list = list.filter((d) => d.consumableChargeId === filter.consumableChargeId);
  }
  if (filter?.consultationId) {
    list = list.filter((d) => d.consultationId === filter.consultationId);
  }
  if (filter?.coverageCaseId) {
    list = list.filter((d) => d.coverageCaseId === filter.coverageCaseId);
  }
  if (filter?.source) {
    list = list.filter((d) => d.source === filter.source);
  }
  if (filter?.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    list = list.filter((d) => statuses.includes(d.status));
  }
  return list.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function getBillingDocument(id: string): BillingDocument | null {
  return readDocuments().find((d) => d.id === id) ?? null;
}

export function getBillingDocumentByPrescription(
  prescriptionId: string,
): BillingDocument | null {
  return (
    listBillingDocuments({ prescriptionId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentByAdmission(
  admissionId: string,
): BillingDocument | null {
  const docs = listBillingDocuments({ admissionId }).filter((d) => d.status !== "cancelled");
  return (
    docs.find((d) => d.source === "consultation") ??
    docs.find((d) => d.source === "prescription" || d.source === "prescription_partial") ??
    docs[0] ??
    null
  );
}

export function getBillingDocumentByAdmissionAndSource(
  admissionId: string,
  source: BillingDocument["source"],
): BillingDocument | null {
  const docs = listBillingDocuments({ admissionId, source }).filter(
    (d) => d.status !== "cancelled",
  );
  if (source === "surgery") {
    return docs.find((d) => !d.surgeryCaseId) ?? docs[0] ?? null;
  }
  return docs[0] ?? null;
}

export function getBillingDocumentByStay(stayId: string): BillingDocument | null {
  const docs = listBillingDocumentsByStay(stayId);
  return docs[0] ?? null;
}

export function listBillingDocumentsByStay(
  stayId: string,
  source?: BillingDocument["source"],
): BillingDocument[] {
  return readDocuments()
    .filter(
      (d) =>
        d.stayId === stayId &&
        d.status !== "cancelled" &&
        (source == null || d.source === source),
    )
    .sort(
      (a, b) =>
        new Date(b.issuedAt ?? b.createdAt).getTime() -
        new Date(a.issuedAt ?? a.createdAt).getTime(),
    );
}

/** Nuitée hospitalisation déjà facturée pour ce séjour et ce jour calendaire. */
export function getHospitalizationBillForStayOnDay(
  stayId: string,
  day: Date,
): BillingDocument | null {
  const target = day.toDateString();
  return (
    listBillingDocumentsByStay(stayId, "hospitalization").find((doc) => {
      const at = new Date(doc.issuedAt ?? doc.createdAt);
      return at.toDateString() === target;
    }) ?? null
  );
}

export function getBillingDocumentByLabOrder(labOrderId: string): BillingDocument | null {
  return (
    listBillingDocuments({ labOrderId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentByImagingExam(imagingExamId: string): BillingDocument | null {
  return (
    listBillingDocuments({ imagingExamId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentBySurgeryCase(surgeryCaseId: string): BillingDocument | null {
  return (
    listBillingDocuments({ surgeryCaseId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentByConsumableCharge(
  consumableChargeId: string,
): BillingDocument | null {
  return (
    listBillingDocuments({ consumableChargeId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentByConsultation(consultationId: string): BillingDocument | null {
  return (
    listBillingDocuments({ consultationId }).find((d) => d.status !== "cancelled") ?? null
  );
}

export function getBillingDocumentForPrescriptionLine(
  prescriptionId: string,
  lineId: string,
): BillingDocument | null {
  return (
    listBillingDocuments({ prescriptionId }).find(
      (d) => d.prescriptionLineIds.includes(lineId) && d.status !== "cancelled",
    ) ?? null
  );
}

const DEFAULT_LAB_EXAM_FEE = 25_000;
const DEFAULT_IMAGING_EXAM_FEE = 45_000;

export function isAdmissionConsultationPaid(admissionId: string): boolean {
  const doc = getBillingDocumentByAdmissionAndSource(admissionId, "consultation");
  return doc?.status === "paid";
}

async function buildLinesFromPrescription(
  prescriptionId: string,
  lineIds?: string[],
): Promise<BillingDocumentLine[]> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return [];

  const selected =
    lineIds && lineIds.length > 0
      ? prescription.lines.filter((l) => lineIds.includes(l.id))
      : prescription.lines;

  const products = await pharmacyInventoryApi.listPharmacyProducts();
  const byId = new Map(products.map((p) => [p.id, p]));

  return selected.map((line) => {
    const product = byId.get(line.productId);
    const unitPrice = product ? (resolveProductPrice(product) ?? 0) : 0;
    return {
      id: lineId(),
      productId: line.productId,
      label: line.productLabel,
      unitPrice,
      quantity: line.quantity,
    };
  });
}

function buildConsultationLine(serviceLabel: string, unitPrice: number): BillingDocumentLine {
  return {
    id: lineId(),
    productId: "",
    label: `Consultation — ${serviceLabel}`,
    unitPrice,
    quantity: 1,
  };
}

function createConsultationDocumentBase(input: {
  patientId: string;
  admissionId: string;
  serviceLabel: string;
  unitPrice: number;
  customerName: string;
  phone?: string;
  address?: string;
}): BillingDocument {
  const patient = getPatientById(input.patientId);
  const payer = resolveBillingPayerFromPatient(input.patientId);
  const line = buildConsultationLine(input.serviceLabel, input.unitPrice);
  const now = new Date().toISOString();

  return {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "consultation",
    kind: "proforma",
    status: "pending",
    number: `BROUILLON-ADM-${input.admissionId.slice(-6)}`,
    patientId: input.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId: null,
    admissionId: input.admissionId,
    serviceLabel: input.serviceLabel,
    payerType: payer.payerType,
    payerName: payer.payerName,
    insurerId: payer.insurerId,
    customer: {
      name: input.customerName,
      phone: input.phone ?? patient?.phone ?? "",
      address: input.address ?? patient?.address ?? "",
    },
    lines: [line],
    subtotal: line.unitPrice * line.quantity,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };
}

function persistDocument(doc: BillingDocument): BillingDocument {
  const list = readDocuments();
  list.unshift(doc);
  writeJson(DOCS_KEY, list);
  replicateBillingDocumentToApi(doc);
  return doc;
}

/** Accueil — proforma consultation (si autorisé par l'admin). */
export function createConsultationProformaAtReception(
  admissionId: string,
  input: { patientId: string; customerName: string; serviceLabel?: string },
): BillingDocument | null {
  if (!getReceptionSettings().allowReceptionConsultationProforma) return null;

  const existing = getBillingDocumentByAdmission(admissionId);
  if (existing) return existing;

  const serviceLabel = input.serviceLabel ?? "Médecine générale";
  const fee = getReceptionSettings().generalConsultationFee;
  const patient = getPatientById(input.patientId);
  const doc = createConsultationDocumentBase({
    patientId: input.patientId,
    admissionId,
    serviceLabel,
    unitPrice: fee,
    customerName: input.customerName,
    phone: patient?.phone,
    address: patient?.address,
  });
  persistDocument(doc);
  const issued = issueProforma(doc.id);
  if (issued) {
    patchDocument(issued.id, { status: "at_cashier" });
    return getBillingDocument(issued.id);
  }
  return getBillingDocument(doc.id);
}

/** Dossier bloqué à l'accueil → comptabilité (sans proforma accueil). */
export function createPendingConsultationBillingDocument(input: {
  admissionId: string;
  patientId: string;
  serviceLabel: string;
  customerName: string;
}): BillingDocument {
  const existing = getBillingDocumentByAdmission(input.admissionId);
  if (existing) return existing;

  const fee = getReceptionSettings().generalConsultationFee;
  const patient = getPatientById(input.patientId);
  const doc = createConsultationDocumentBase({
    patientId: input.patientId,
    admissionId: input.admissionId,
    serviceLabel: input.serviceLabel,
    unitPrice: fee,
    customerName: input.customerName,
    phone: patient?.phone,
    address: patient?.address,
  });
  return persistDocument(doc);
}

/** Accueil bloqué (pending_billing) → dossier compta ; proforma caisse si payeur patient. */
export function bootstrapAdmissionPendingBilling(input: {
  admissionId: string;
  patientId: string;
  serviceLabel: string;
  customerName: string;
}): BillingDocument {
  const doc = createPendingConsultationBillingDocument(input);
  if (doc.payerType === "patient" && doc.status === "pending") {
    const issued = issueProforma(doc.id);
    if (issued) {
      return patchDocument(issued.id, { status: "at_cashier" }) ?? doc;
    }
  }
  return getBillingDocument(doc.id) ?? doc;
}

/** Pharmacien → comptabilité : crée le dossier à facturer (ordonnance complète). */
export async function createBillingDocumentFromPrescription(
  prescriptionId: string,
): Promise<BillingDocument | null> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;
  if (prescription.status !== "pharmacist_validated") return null;

  const lineIds = prescription.lines
    .filter((l) => l.fulfillment?.stockState === "reserved" && !l.fulfillment.billingDocumentId)
    .map((l) => l.id);
  if (lineIds.length === 0) {
    const legacy = prescription.lines.map((l) => l.id);
    return createBillingDocumentFromPrescriptionLines(prescriptionId, legacy);
  }
  return createBillingDocumentFromPrescriptionLines(prescriptionId, lineIds);
}

/** Pharmacien → comptabilité : lignes sélectionnées (facturation partielle). */
export async function createBillingDocumentFromPrescriptionLines(
  prescriptionId: string,
  lineIds: string[],
): Promise<BillingDocument | null> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription || lineIds.length === 0) return null;

  const existingForLine = lineIds
    .map((id) => getBillingDocumentForPrescriptionLine(prescriptionId, id))
    .find(Boolean);
  if (existingForLine) return existingForLine;

  const patient = getPatientById(prescription.patientId);
  const payer = resolveBillingPayerFromPatient(prescription.patientId);
  const lines = await buildLinesFromPrescription(prescriptionId, lineIds);
  if (lines.length === 0) return null;

  const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: lineIds.length === prescription.lines.length ? "prescription" : "prescription_partial",
    kind: "invoice",
    status: "pending",
    number: `BROUILLON-${prescriptionId.slice(-6)}-${lineIds.length}`,
    patientId: prescription.patientId,
    prescriptionId,
    prescriptionLineIds: [...lineIds],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId: getCoverageCaseForPrescription(prescriptionId)?.id ?? null,
    admissionId: null,
    serviceLabel: "",
    payerType: payer.payerType,
    payerName: payer.payerName,
    insurerId: payer.insurerId,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines,
    subtotal,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);

  if (payer.payerType === "insurer" && payer.insurerId) {
    const issued = await issueInvoiceDirect(doc.id);
    return issued ?? doc;
  }

  return issuePatientProformaAtCashier(doc.id) ?? doc;
}

async function buildLinesFromConsumableCharge(
  chargeId: string,
): Promise<BillingDocumentLine[]> {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge) return [];

  const products = await pharmacyInventoryApi.listProductsForDepot("consumables");
  const byId = new Map(products.map((p) => [p.id, p]));

  return charge.lines.map((line) => {
    const product = byId.get(line.productId);
    const unitPrice = product ? (resolveProductPrice(product) ?? 0) : 0;
    return {
      id: lineId(),
      productId: line.productId,
      label: line.productLabel,
      unitPrice,
      quantity: line.quantity,
    };
  });
}

/** Soins / hospitalisation → comptabilité : consommables patient (WH-CONS). */
export async function createBillingDocumentFromConsumableCharge(
  chargeId: string,
): Promise<BillingDocument | null> {
  const charge = getPatientConsumableChargeById(chargeId);
  if (!charge || charge.status !== "draft") return null;
  if (charge.lines.length === 0) return null;

  const existing = getBillingDocumentByConsumableCharge(chargeId);
  if (existing) return existing;

  const patient = getPatientById(charge.patientId);
  const payer = resolveBillingPayerFromPatient(charge.patientId);
  const lines = await buildLinesFromConsumableCharge(chargeId);
  if (lines.length === 0) return null;

  const subtotal = lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "patient_consumables",
    kind: "invoice",
    status: "pending",
    number: `BROUILLON-CONS-${charge.chargeNumber}`,
    patientId: charge.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: chargeId,
    consultationId: null,
    coverageCaseId: null,
    admissionId: charge.admissionId,
    stayId: charge.stayId,
    serviceLabel: "Consommables patient",
    payerType: payer.payerType,
    payerName: payer.payerName,
    insurerId: payer.insurerId,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines,
    subtotal,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);

  if (payer.payerType === "insurer" && payer.insurerId) {
    const issued = await issueInvoiceDirect(doc.id);
    return issued ?? doc;
  }

  return issuePatientProformaAtCashier(doc.id) ?? doc;
}

function patchDocument(id: string, patch: Partial<BillingDocument>): BillingDocument | null {
  const list = readDocuments();
  const index = list.findIndex((d) => d.id === id);
  if (index < 0) return null;
  const updated: BillingDocument = {
    ...list[index]!,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  list[index] = updated;
  writeJson(DOCS_KEY, list);
  replicateBillingDocumentToApi(updated);
  return updated;
}

export function updateBillingDocumentPayer(
  id: string,
  payerType: PayerType,
): BillingDocument | null {
  const doc = getBillingDocument(id);
  if (!doc) return null;

  const patient = getPatientById(doc.patientId);
  if (payerType === "insurer") {
    if (!patient?.insurerId) return null;
    if (!getActivePartnershipForInsurer(patient.insurerId)) return null;
    const insurer = getInsurerById(patient.insurerId);
    return patchDocument(id, {
      payerType: "insurer",
      payerName: insurer?.name ?? patient.coverageName,
      insurerId: patient.insurerId,
    });
  }

  if (payerType === "company" && patient?.coverageType === "company") {
    return patchDocument(id, {
      payerType: "company",
      payerName: patient.coverageName,
      insurerId: null,
    });
  }

  return patchDocument(id, {
    payerType: "patient",
    payerName: patient ? patientFullName(patient) : doc.customer.name,
    insurerId: null,
  });
}

export function syncBillingDocumentPayerFromPatient(id: string): BillingDocument | null {
  const doc = getBillingDocument(id);
  if (!doc) return null;
  const payer = resolveBillingPayerFromPatient(doc.patientId);
  return patchDocument(id, payer);
}

/** Facture tiers payant assureur — partenariat hub actif obligatoire. */
export function insurerHubReadyForDocument(doc: BillingDocument): {
  ready: boolean;
  reason?: string;
} {
  if (doc.payerType !== "insurer" || !doc.insurerId) {
    return { ready: true };
  }
  const partnership = getActivePartnershipForInsurer(doc.insurerId);
  if (!partnership) {
    return {
      ready: false,
      reason:
        "Partenariat hub requis — configurez et activez le lien dans Assurance → Partenariats hub.",
    };
  }
  return { ready: true };
}

function assertInsurerHubReady(doc: BillingDocument): void {
  const check = insurerHubReadyForDocument(doc);
  if (!check.ready) {
    throw new BillingInsurerHubError(check.reason ?? "Partenariat hub requis.");
  }
}

async function finalizeInsurerInvoiceOnHub(issued: BillingDocument): Promise<BillingDocument> {
  if (issued.payerType !== "insurer") return issued;

  const published = await publishBillingDocumentToHub(issued);
  if (!published || published.via !== "api") {
    throw new BillingInsurerHubError(
      published?.error ??
        "Publication hub échouée — démarrez l'API assureur (port 3120) et vérifiez le partenariat actif.",
    );
  }

  const updated = patchDocument(issued.id, {
    hubInvoiceId: published.invoiceId,
    hubPublishedAt: new Date().toISOString(),
  });
  return updated ?? issued;
}

export function issueProforma(id: string, validDays = 7): BillingDocument | null {
  const doc = getBillingDocument(id);
  if (!doc || doc.status !== "pending") return null;
  const validUntil = new Date();
  validUntil.setDate(validUntil.getDate() + validDays);
  const now = new Date().toISOString();
  return patchDocument(id, {
    kind: "proforma",
    status: "proforma",
    number: nextProformaNumber(),
    issuedAt: now,
    proformaValidUntil: validUntil.toISOString(),
  });
}

export async function convertProformaToInvoice(id: string): Promise<BillingDocument | null> {
  const doc = getBillingDocument(id);
  if (!doc || doc.status !== "proforma") return null;
  assertInsurerHubReady(doc);

  const now = new Date().toISOString();
  const issued = patchDocument(id, {
    kind: "invoice",
    status: "issued",
    number: nextInvoiceNumber(),
    issuedAt: now,
    convertedFromId: doc.id,
    proformaValidUntil: null,
  });
  if (!issued) return null;

  try {
    return await finalizeInsurerInvoiceOnHub(issued);
  } catch (err) {
    patchDocument(id, {
      kind: "proforma",
      status: "proforma",
      number: doc.number,
      issuedAt: doc.issuedAt,
      convertedFromId: doc.convertedFromId,
      proformaValidUntil: doc.proformaValidUntil,
      hubInvoiceId: null,
      hubPublishedAt: null,
    });
    throw err;
  }
}

export async function issueInvoiceDirect(id: string): Promise<BillingDocument | null> {
  const doc = getBillingDocument(id);
  if (!doc || doc.status !== "pending") return null;
  assertInsurerHubReady(doc);

  const now = new Date().toISOString();
  const issued = patchDocument(id, {
    kind: "invoice",
    status: "issued",
    number: nextInvoiceNumber(),
    issuedAt: now,
  });
  if (!issued) return null;

  try {
    return await finalizeInsurerInvoiceOnHub(issued);
  } catch (err) {
    patchDocument(id, {
      kind: "invoice",
      status: "pending",
      number: doc.number,
      issuedAt: null,
      hubInvoiceId: null,
      hubPublishedAt: null,
    });
    throw err;
  }
}

/** Caisse / comptabilité — règlement simplifié (sans trésorerie). */
export function markBillingDocumentPaid(id: string): BillingDocument | null {
  const doc = getBillingDocument(id);
  if (!doc) return null;
  if (!["proforma", "issued", "at_cashier", "pending"].includes(doc.status)) return null;

  const now = new Date().toISOString();
  const updated = patchDocument(id, {
    status: "paid",
    kind: doc.kind === "proforma" ? "invoice" : doc.kind,
    number: doc.status === "pending" || doc.number.startsWith("BROUILLON") ? nextInvoiceNumber() : doc.number,
    issuedAt: doc.issuedAt ?? now,
  });
  if (updated?.source === "consultation" && updated.admissionId) {
    dispatchConsultationPaidIfNeeded(updated);
  }
  if (updated?.prescriptionId) {
    handleBillingDocumentPaidForPrescription(updated.id);
  }
  if (updated?.consumableChargeId) {
    handleBillingDocumentPaidForConsumableCharge(updated.id);
  }
  return updated;
}

export function countPendingBillingDocuments(): number {
  return listBillingDocuments({ status: ["pending", "proforma", "at_cashier"] }).length;
}

const CASHIER_STATUSES: BillingDocumentStatus[] = [
  "at_cashier",
  "proforma",
  "issued",
  "pending",
];

/** File d'attente caisse — documents patient/entreprise à encaisser. */
export function listCashierQueue(): BillingDocument[] {
  return listBillingDocuments({ status: CASHIER_STATUSES }).filter(
    (doc) => doc.payerType !== "insurer",
  );
}

export function countCashierQueue(): number {
  return listCashierQueue().length;
}

function dispatchConsultationPaidIfNeeded(doc: BillingDocument): void {
  if (doc.source === "consultation" && doc.admissionId) {
    window.dispatchEvent(
      new CustomEvent("hospitol:consultation-paid", { detail: { document: doc } }),
    );
  }
}

export interface RecordBillingPaymentInput {
  method: PaymentMethod;
  treasuryAccountId: string;
  amount: number;
  reference?: string;
  attachment?: BillingPaymentAttachment | null;
  paidBy?: string;
}

/** Caisse — enregistre le paiement, crédite le compte et marque la facture payée. */
export function recordBillingPayment(
  id: string,
  input: RecordBillingPaymentInput,
): BillingDocument | null {
  const doc = getBillingDocument(id);
  if (!doc) return null;
  if (!CASHIER_STATUSES.includes(doc.status)) return null;
  if (doc.payerType === "insurer") return null;

  const account = getTreasuryAccount(input.treasuryAccountId);
  if (!account) return null;

  const amount = input.amount > 0 ? input.amount : doc.subtotal;
  const now = new Date().toISOString();

  const credited = creditTreasuryAccount({
    accountId: account.id,
    amount,
    label: `Encaissement ${doc.number}`,
    billingDocumentId: doc.id,
    paymentMethod: input.method,
    reference: input.reference,
  });
  if (!credited) return null;

  const payment: BillingPayment = {
    method: input.method,
    treasuryAccountId: account.id,
    treasuryAccountName: account.name,
    amount,
    reference: input.reference?.trim() ?? "",
    attachment: input.attachment ?? null,
    paidAt: now,
    paidBy: input.paidBy?.trim() || "Caissier",
  };

  const updated = patchDocument(id, {
    status: "paid",
    kind: doc.kind === "proforma" ? "invoice" : doc.kind,
    number:
      doc.status === "pending" || doc.number.startsWith("BROUILLON")
        ? nextInvoiceNumber()
        : doc.number,
    issuedAt: doc.issuedAt ?? now,
    payment,
  });

  if (updated) {
    dispatchConsultationPaidIfNeeded(updated);
    if (updated.prescriptionId) {
      handleBillingDocumentPaidForPrescription(updated.id);
    }
    if (updated.consumableChargeId) {
      handleBillingDocumentPaidForConsumableCharge(updated.id);
    }
  }
  return updated;
}

function issuePatientProformaAtCashier(docId: string): BillingDocument | null {
  const issued = issueProforma(docId);
  if (!issued) return getBillingDocument(docId);
  return patchDocument(issued.id, { status: "at_cashier" });
}

/** Proforma patient pour une seule ligne d'ordonnance (médicament non validé assureur). */
export async function createPartialPatientProformaFromPrescriptionLine(
  prescriptionId: string,
  prescriptionLineId: string,
): Promise<BillingDocument | null> {
  const prescription = getPrescriptionById(prescriptionId);
  if (!prescription) return null;
  const line = prescription.lines.find((l) => l.id === prescriptionLineId);
  if (!line) return null;

  const existing = getBillingDocumentForPrescriptionLine(prescriptionId, prescriptionLineId);
  if (existing) return existing;

  const patient = getPatientById(prescription.patientId);
  const products = await pharmacyInventoryApi.listPharmacyProducts();
  const product = products.find((p) => p.id === line.productId);
  const unitPrice = product ? (resolveProductPrice(product) ?? 0) : 0;
  const billingLine: BillingDocumentLine = {
    id: lineId(),
    productId: line.productId,
    label: line.productLabel,
    unitPrice,
    quantity: line.quantity,
  };
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "prescription_partial",
    kind: "proforma",
    status: "pending",
    number: `BROUILLON-RXL-${prescriptionLineId.slice(-6)}`,
    patientId: prescription.patientId,
    prescriptionId,
    prescriptionLineIds: [prescriptionLineId],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId: null,
    admissionId: null,
    serviceLabel: "",
    payerType: "patient",
    payerName: patient ? patientFullName(patient) : "Patient",
    insurerId: null,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines: [billingLine],
    subtotal: billingLine.unitPrice * billingLine.quantity,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);
  return issuePatientProformaAtCashier(doc.id);
}

/** Proforma patient pour une analyse laboratoire (auto-paiement en attente assureur). */
export function createPatientProformaForLabOrder(labOrderId: string): BillingDocument | null {
  const order = getLabOrderById(labOrderId);
  if (!order) return null;

  const existing = getBillingDocumentByLabOrder(labOrderId);
  if (existing) return existing;

  const patient = getPatientById(order.patientId);
  const line = buildConsultationLine(`Laboratoire — ${order.testName}`, DEFAULT_LAB_EXAM_FEE);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "laboratory",
    kind: "proforma",
    status: "pending",
    number: `BROUILLON-LAB-${order.orderNumber}`,
    patientId: order.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId: order.coverageCaseId,
    admissionId: null,
    serviceLabel: order.testName,
    payerType: "patient",
    payerName: patient ? patientFullName(patient) : "Patient",
    insurerId: null,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines: [line],
    subtotal: line.unitPrice,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);
  return issuePatientProformaAtCashier(doc.id);
}

function findExistingActBillingDocument(input: {
  source: BillingDocument["source"];
  consultationId?: string | null;
  labOrderId?: string | null;
  imagingExamId?: string | null;
  surgeryCaseId?: string | null;
  admissionId?: string | null;
  stayId?: string | null;
}): BillingDocument | null {
  if (input.surgeryCaseId) {
    const caseDoc = getBillingDocumentBySurgeryCase(input.surgeryCaseId);
    if (caseDoc && caseDoc.source === input.source) return caseDoc;
  }
  if (input.source !== "hospitalization" && input.stayId) {
    const stayDoc = getBillingDocumentByStay(input.stayId);
    if (stayDoc && stayDoc.source === input.source) return stayDoc;
  }
  if (input.admissionId) {
    if (input.source === "surgery" && !input.surgeryCaseId) {
      const orientDoc = listBillingDocuments({ admissionId: input.admissionId, source: "surgery" })
        .find((d) => !d.surgeryCaseId && d.status !== "cancelled");
      if (orientDoc) return orientDoc;
    } else {
      const admDoc = getBillingDocumentByAdmissionAndSource(input.admissionId, input.source);
      if (admDoc) return admDoc;
    }
  }
  if (input.consultationId) {
    const doc = getBillingDocumentByConsultation(input.consultationId);
    if (doc) return doc;
  }
  if (input.labOrderId) {
    const doc = getBillingDocumentByLabOrder(input.labOrderId);
    if (doc) return doc;
  }
  if (input.imagingExamId) {
    const doc = getBillingDocumentByImagingExam(input.imagingExamId);
    if (doc) return doc;
  }
  return null;
}

async function finalizeAutoBillingDocument(doc: BillingDocument): Promise<BillingDocument | null> {
  if (doc.status === "issued" || doc.status === "paid") return doc;

  if (doc.payerType === "insurer" || doc.payerType === "company") {
    try {
      return await issueInvoiceDirect(doc.id);
    } catch (err) {
      console.warn("[Hospitol] Auto-facturation tiers payant échouée", err);
      window.dispatchEvent(
        new CustomEvent("hospitol:billing-auto-error", {
          detail: {
            documentId: doc.id,
            message: err instanceof Error ? err.message : String(err),
          },
        }),
      );
      return getBillingDocument(doc.id);
    }
  }

  return issuePatientProformaAtCashier(doc.id);
}

/** Crée et émet automatiquement la facture (assureur/entreprise) ou proforma patient après un acte. */
export async function createAndIssueActBillingDocument(input: {
  patientId: string;
  source: Exclude<
    BillingDocument["source"],
    "prescription" | "prescription_partial" | "patient_consumables"
  >;
  serviceLabel: string;
  unitPrice: number;
  linePrefix: string;
  admissionId?: string | null;
  stayId?: string | null;
  consultationId?: string | null;
  labOrderId?: string | null;
  imagingExamId?: string | null;
  surgeryCaseId?: string | null;
  coverageCaseId?: string | null;
  forcePatientPayer?: boolean;
  /** Hospitalisation : autorise plusieurs factures par séjour (une par jour). */
  allowMultiplePerStay?: boolean;
}): Promise<BillingDocument | null> {
  if (input.surgeryCaseId) {
    const existingCase = getBillingDocumentBySurgeryCase(input.surgeryCaseId);
    if (existingCase) {
      if (["pending", "proforma", "at_cashier"].includes(existingCase.status)) {
        return finalizeAutoBillingDocument(existingCase);
      }
      return existingCase;
    }
  } else if (!input.allowMultiplePerStay) {
    const existing = findExistingActBillingDocument(input);
    if (existing) {
      if (["pending", "proforma", "at_cashier"].includes(existing.status)) {
        return finalizeAutoBillingDocument(existing);
      }
      return existing;
    }
  }

  const patient = getPatientById(input.patientId);
  const payer = input.forcePatientPayer
    ? {
        payerType: "patient" as PayerType,
        payerName: patient ? patientFullName(patient) : "Patient",
        insurerId: null,
      }
    : resolveBillingPayerFromPatient(input.patientId);

  const line = buildConsultationLine(`${input.linePrefix} — ${input.serviceLabel}`, input.unitPrice);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: input.source,
    kind: "invoice",
    status: "pending",
    number: `BROUILLON-ACT-${Date.now().toString(36).slice(-6)}`,
    patientId: input.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: input.labOrderId ?? null,
    imagingExamId: input.imagingExamId ?? null,
    surgeryCaseId: input.surgeryCaseId ?? null,
    consumableChargeId: null,
    consultationId: input.consultationId ?? null,
    coverageCaseId: input.coverageCaseId ?? null,
    admissionId: input.admissionId ?? null,
    stayId: input.stayId ?? null,
    serviceLabel: input.serviceLabel,
    payerType: payer.payerType,
    payerName: payer.payerName,
    insurerId: payer.insurerId,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines: [line],
    subtotal: line.unitPrice * line.quantity,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);
  return finalizeAutoBillingDocument(doc);
}

/** Proforma patient pour un examen imagerie (auto-paiement en attente assureur). */
export function createPatientProformaForImagingExam(imagingExamId: string): BillingDocument | null {
  const exam = getImagingExamById(imagingExamId);
  if (!exam) return null;

  const existing = getBillingDocumentByImagingExam(imagingExamId);
  if (existing) return existing;

  const patient = getPatientById(exam.patientId);
  const label = exam.bodyPart ? `${exam.examType} — ${exam.bodyPart}` : exam.examType;
  const line = buildConsultationLine(`Imagerie — ${label}`, DEFAULT_IMAGING_EXAM_FEE);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "imaging",
    kind: "proforma",
    status: "pending",
    number: `BROUILLON-IMG-${exam.examNumber}`,
    patientId: exam.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: null,
    imagingExamId,
    consultationId: exam.consultationId,
    coverageCaseId: exam.coverageCaseId,
    admissionId: null,
    serviceLabel: label,
    payerType: "patient",
    payerName: patient ? patientFullName(patient) : "Patient",
    insurerId: null,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines: [line],
    subtotal: line.unitPrice,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);
  return issuePatientProformaAtCashier(doc.id);
}

/** Proforma patient depuis un dossier assurance refusé ou en attente (labo, imagerie, ordonnance). */
export async function createPatientProformaFromCoverageCase(
  coverageCaseId: string,
): Promise<BillingDocument | null> {
  const coverageCase = getCoverageCase(coverageCaseId);
  if (!coverageCase) return null;

  const existing = listBillingDocuments({ coverageCaseId }).find((d) => d.status !== "cancelled");
  if (existing) return existing;

  if (coverageCase.labOrderId) {
    const doc = createPatientProformaForLabOrder(coverageCase.labOrderId);
    if (doc) {
      return patchDocument(doc.id, { coverageCaseId });
    }
    return null;
  }

  if (coverageCase.imagingExamId) {
    const doc = createPatientProformaForImagingExam(coverageCase.imagingExamId);
    if (doc) {
      return patchDocument(doc.id, { coverageCaseId });
    }
    return null;
  }

  if (coverageCase.prescriptionId) {
    const rx = getPrescriptionById(coverageCase.prescriptionId);
    if (!rx || rx.lines.length === 0) return null;
    const doc = await createPartialPatientProformaFromPrescriptionLine(
      coverageCase.prescriptionId,
      rx.lines[0]!.id,
    );
    if (doc) {
      return patchDocument(doc.id, { coverageCaseId });
    }
    return null;
  }

  const patient = getPatientById(coverageCase.patientId);
  const line = buildConsultationLine(coverageCase.label, DEFAULT_LAB_EXAM_FEE);
  const now = new Date().toISOString();

  const doc: BillingDocument = {
    id: `bd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    source: "laboratory",
    kind: "proforma",
    status: "pending",
    number: `BROUILLON-INS-${coverageCaseId.slice(-6)}`,
    patientId: coverageCase.patientId,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId,
    admissionId: null,
    serviceLabel: coverageCase.label,
    payerType: "patient",
    payerName: patient ? patientFullName(patient) : "Patient",
    insurerId: null,
    customer: {
      name: patient ? patientFullName(patient) : "Patient",
      phone: patient?.phone ?? "",
      address: patient?.address ?? "",
    },
    lines: [line],
    subtotal: line.unitPrice,
    createdAt: now,
    updatedAt: now,
    issuedAt: null,
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: null,
  };

  persistDocument(doc);
  return issuePatientProformaAtCashier(doc.id);
}
